import React, { useCallback, useEffect, useRef } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { initDB } from '../../../../libraries/storage/idb';
import { loggerAtom } from '../../../../modernization-common/logger/logger-atom';
import { annotationLabelsAtom, annotationMetadataAtom, frameCountAtom } from '../../atoms/annotation-atoms';
import { interpolationManagerAtom } from '../../atoms/interpolation';
import { studioStoreAtom } from '../../atoms/studio-store';
import type { AnnotationFrameWriteEntry } from '../../atoms/studio-store-write/actions';
import { STUDIO_WRITE_ACTIONS } from '../../atoms/studio-store-write/actions';
import { dispatchStudioStoreWriteAtom } from '../../atoms/studio-store-write/dispatch-studio-store-write';
import { InterpolationManager } from '../../interpolation-manager';
import type { StudioConfigT } from '../types';
import { StudioStore } from '..';
type PropsT = {
  studioConfig: StudioConfigT;
  children: React.ReactNode;
};
export const StudioStoreInitializer = ({
  studioConfig,
  children
}: PropsT) => {
  const setStudioStore = useSetAtom(studioStoreAtom);
  const setInterpolationManager = useSetAtom(interpolationManagerAtom);
  const frameCount = useAtomValue(frameCountAtom);
  const logger = useAtomValue(loggerAtom);
  const studioConfigRef = useRef(studioConfig);
  // eslint-disable-next-line react-hooks/refs
  studioConfigRef.current = studioConfig;
  const getAnnotationMeta = useAtomCallback(useCallback((get, _set, annotationId: string) => {
    const metadata = get(annotationMetadataAtom).get(annotationId);
    const labels = get(annotationLabelsAtom);
    const label = labels.find(l => l.title === metadata?.labelId);
    return metadata && label ? {
      label,
      annotationType: metadata.annotationType,
      answers: metadata.answers,
      dynamicAnswers: metadata.dynamicAnswers
    } : null;
  }, []));
  const doBulkWrite = useAtomCallback(useCallback((_get, set, writes: AnnotationFrameWriteEntry[]) => {
    set(dispatchStudioStoreWriteAtom, {
      type: STUDIO_WRITE_ACTIONS.BULK_WRITE,
      writes
    });
  }, []));
  useEffect(() => {
    if (frameCount <= 0) {
      return;
    }
    const store = new StudioStore({
      config: studioConfigRef.current
    });
    const manager = new InterpolationManager(getAnnotationMeta, doBulkWrite);
    let active = true;
    const onBeforeUnload = () => store.flush();
    window.addEventListener('beforeunload', onBeforeUnload);
    setInterpolationManager(manager);
    initDB().then(() => store.init(frameCount)).then(() => {
      if (active) {
        setStudioStore(store);
      }
    }).catch(error => {
      logger?.error('Error initializing studio store', error);
    });
    return () => {
      active = false;
      window.removeEventListener('beforeunload', onBeforeUnload);
      store.flush();
      setStudioStore(null);
      setInterpolationManager(null);
    };
  }, [frameCount, setStudioStore, setInterpolationManager, getAnnotationMeta, doBulkWrite, logger]);
  return <>{children}</>;
};